import { TemplateParameterSchema } from '@mainstage/plugin-scaffolder-react';
import { useDefaultValidations } from './useDefaultValidations';
import { transformSchemaToProps } from './schema';
import { panelCatalogApiRef } from '@agilelab/plugin-wb-builder-catalog';
import { configApiRef, useApi, useApiHolder } from '@mainstage/core-plugin-api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { LayoutOptions } from '../../layouts';
import { createValidator, createValidatorAsync } from '../TemplatePage';
import validator from '@rjsf/validator-ajv8';
import { getValidatorsFromPrecompiledCode } from './precompiledValidator';
import { Step } from './utils';
import { JsonObject } from '@mainstage/types';
import XXH from 'xxhashjs';

const computeHash = (input: JsonObject[]) => {
  const inputStr = JSON.stringify(input);
  return XXH.h64(inputStr, 0).toString(16);
};

/**
 * Takes an array of raw steps (with merged schema and uiSchema) and returns processed steps with separated schema and uiSchema (resolved with layouts),
 * json schema validators and extra sync and async validation functions.
 */
export const useFormSteps = (
  rawSteps: TemplateParameterSchema['steps'] | null | undefined,
  context: { layouts?: LayoutOptions[] },
) => {
  const panelCatalogApi = useApi(panelCatalogApiRef);

  const apiHolder = useApiHolder();

  const [validators, setValidators] = useState<
    | {
        schemasHash: string;
        validators: Awaited<
          ReturnType<typeof getValidatorsFromPrecompiledCode>
        >;
      }
    | undefined
  >(undefined);

  const configApi = useApi(configApiRef);

  const precompiledFormSchemaValidation =
    configApi.getOptionalBoolean(
      'mesh.builder.ui.experimentalPrecompiledFormSchemaValidation',
    ) ?? true;

  const { customFieldValidatorsAsync, customFieldValidatorsSync } =
    useDefaultValidations();

  const schemas = useMemo(() => {
    if (!rawSteps) return { splittedSchemas: [], pureSchemas: [] };
    const splittedSchemas = rawSteps.map(step =>
      transformSchemaToProps(step.schema, context.layouts),
    );
    // schemas without ui properties
    const pureSchemas = splittedSchemas.map(
      ({ schema: currSchema }) => currSchema,
    );
    return { splittedSchemas, pureSchemas };
  }, [rawSteps, context.layouts]);

  // use a hash of the schemas as an identifier to fetch the validators,
  // we should request compiled validators from backend for each hash only once (unless cache evicted)
  const schemasHash = useMemo(
    () => computeHash(schemas.pureSchemas),
    [schemas.pureSchemas],
  );

  const {
    data: validatorsCode,
    isLoading: validatorsCodeLoading,
    error,
  } = useQuery({
    // cache key is the hash of the schemas, so only if the schemas change we will fetch new validators
    queryKey: ['schemaValidators', schemasHash],
    staleTime: 5 * 60 * 1000, // Cache the result for 5 minutes
    enabled: !!rawSteps && precompiledFormSchemaValidation, // Only run the query if rawSteps is available and using precompiled templates
    queryFn: async ({ signal }) => {
      const res = await panelCatalogApi.computeSchemaValidators(
        {
          specParameters: schemas.pureSchemas,
        },
        { signal },
      );
      return res;
    },
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!validatorsCode || validatorsCodeLoading) return;

      // transform validators code to rjsf validator instances with the corresponding schema which can be passed to the Form component
      // they work without unsafe-eval in cps
      const result = await getValidatorsFromPrecompiledCode(
        validatorsCode.validators.map((curr, i) => ({
          code: curr.code,
          schema: schemas.pureSchemas[i],
          id: `validator-${schemasHash}-${i}`,
        })),
      );
      if (cancelled) return;

      setValidators({
        schemasHash,
        validators: result,
      });
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [validatorsCode, schemasHash, schemas.pureSchemas, validatorsCodeLoading]);

  // If the schemas have changed since the validators were generated, consider them stale and show loading state until new validators are ready
  const validatorsStale = schemasHash !== validators?.schemasHash;

  const processedSteps = useMemo<Step[] | undefined>(() => {
    let usedValidators = validators?.validators;
    // fallback to standard non-precompiled validator if precompiled validators are not enabled (needs unsafe-eval to work)
    if (!precompiledFormSchemaValidation)
      usedValidators = new Array(schemas.pureSchemas.length).fill(validator);

    if (!rawSteps || !usedValidators) return undefined;

    return rawSteps.map((step, index) => {
      const mergedSchema = step.schema;
      const pureSchema = schemas.pureSchemas[index];
      return {
        ...step,
        mergedSchema: step.schema,
        schema: pureSchema,
        uiSchema: schemas.splittedSchemas[index].uiSchema,
        asyncValidate: createValidatorAsync(
          // for async validation we need the merged schema with ui properties, since we need to apply validation logic based on the picker
          mergedSchema,
          customFieldValidatorsAsync,
          { apiHolder },
        ),
        customValidate: createValidator(
          // for sync validation we need the merged schema with ui properties, since we need to apply validation logic based on the picker
          mergedSchema,
          customFieldValidatorsSync,
          {
            apiHolder,
          },
        ),
        validator: usedValidators[index],
      };
    });
  }, [
    customFieldValidatorsAsync,
    customFieldValidatorsSync,
    schemas,
    apiHolder,
    rawSteps,
    validators,
    precompiledFormSchemaValidation,
  ]);

  // if not using precompiled validators, there is no loading state
  const loading =
    precompiledFormSchemaValidation &&
    (validatorsCodeLoading || validatorsStale);

  return {
    steps: processedSteps,
    loading,
    error,
  };
};
